import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { PARKING_LOT_COLUMNS, type ParkingLotColumn, type ParkingLotItem } from '@jabberr/core';
import { ParkingNote } from '../components/ParkingNote.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { BOARD_COLUMNS, cardMeta, chipLabel, isNewProjectIdea, projectTint } from '../lib/parking.js';
import { setPreferences, usePreference } from '../lib/preferences.js';
import { useDrawer } from '../state/drawer.js';
import { useSession } from '../state/session.js';

type ColourBy = 'column' | 'project';


/**
 * The parking lot (design I-Board): ideas as sticky notes in Someday, Next up and Building, with Not doing folded
 * away. The team sees everything; anyone else sees their own ideas and the ideas of the client projects they are in.
 * /parking-lot/:itemId opens one idea over the board.
 */
export function ParkingLotPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { projects } = useSession();
  const { setInfo } = useDrawer();
  const [items, setItems] = useState<ParkingLotItem[] | null>(null);
  const [scope, setScope] = useState<'team' | 'own'>('own');
  const [error, setError] = useState<string | null>(null);
  const [showNotDoing, setShowNotDoing] = useState(false);
  const colourBy: ColourBy = usePreference('parkingColourBy') ?? 'column';
  const [adding, setAdding] = useState(false);
  const filter = params.get('project') ?? '';
  const team = scope === 'team';

  useEffect(() => {
    setInfo({ title: 'Parking lot' });
  }, [setInfo]);

  const load = useCallback(() => {
    api.parkingLot
      .list(filter ? { project: filter } : {})
      .then((r) => {
        setItems(r.items);
        setScope(r.scope);
      })
      .catch((err) => setError(errorMessage(err)));
  }, [filter]);
  useEffect(() => {
    setItems(null);
    load();
  }, [load]);

  const choices = useMemo(
    () => (projects as Array<{ id: string; name: string; kind: string; role?: string }>).filter((p) => p.kind === 'team').sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );
  const byColumn = (c: ParkingLotColumn) => (items ?? []).filter((i) => i.column === c);
  const notDoing = byColumn('not_doing');
  const search = location.search;
  const close = useCallback(() => navigate(`/parking-lot${search}`), [navigate, search]);

  const setColour = (v: ColourBy): void => setPreferences({ parkingColourBy: v });

  const column = (c: ParkingLotColumn) => {
    const list = byColumn(c);
    return (
      <section key={c} className={`park-col park-col-${c}`} aria-label={PARKING_LOT_COLUMNS[c]}>
        <h2>
          {PARKING_LOT_COLUMNS[c]} <span className="park-count">{list.length}</span>
        </h2>
        {list.map((item, i) => (
          <Card key={item.id} item={item} index={i} colourBy={colourBy} team={team} search={search} />
        ))}
        {list.length === 0 ? <p className="park-empty">Nothing here yet.</p> : null}
        {c === 'building' ? <p className="park-foot">Ideas move here once their requirements are approved in Loop.</p> : null}
      </section>
    );
  };

  return (
    <div className="page parking-page">
      <header className="park-head">
        <div className="park-intro">
          <h1>Parking lot</h1>
          <p>
            {team
              ? 'Ideas we might build, from the whole team. Guests see only their own. Open one to read more and talk about it, or press Build this.'
              : 'Your ideas, and the ideas shared in your projects. Open one to read more and talk about it.'}
          </p>
        </div>
        <div className="park-controls">
          <label className="sr-only" htmlFor="idea-filter">
            Show
          </label>
          <select
            id="idea-filter"
            value={filter}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('project', e.target.value);
              else next.delete('project');
              setParams(next, { replace: true });
            }}
          >
            <option value="">{team ? 'Everything (Tarawa)' : 'Everything I can see'}</option>
            <option value="new">New project ideas</option>
            {choices.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor="idea-colour">
            Colour notes by
          </label>
          <select id="idea-colour" value={colourBy} onChange={(e) => setColour(e.target.value as ColourBy)}>
            <option value="column">Colour by column</option>
            <option value="project">Colour by project</option>
          </select>
          <button type="button" className="btn btn-primary park-new" onClick={() => setAdding(true)}>
            New idea
          </button>
        </div>
      </header>

      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {items === null && !error ? <Spinner label="Loading the parking lot" /> : null}

      {items ? (
        <>
          <div className="park-board">{BOARD_COLUMNS.map(column)}</div>
          <div className="park-notdoing">
            <button type="button" className="link-btn small" aria-expanded={showNotDoing} onClick={() => setShowNotDoing((v) => !v)}>
              {showNotDoing ? 'Hide' : 'Show'} Not doing · {notDoing.length}
            </button>
            {showNotDoing ? <div className="park-board park-board-single">{column('not_doing')}</div> : null}
          </div>
        </>
      ) : null}

      {itemId ? <ParkingNote itemId={itemId} team={team} colourBy={colourBy} onClose={close} onChanged={load} /> : null}
      {adding ? (
        <NewIdea
          team={team}
          projects={projects as Array<{ id: string; name: string; kind: string; role?: string }>}
          defaultProject={filter && filter !== 'new' ? filter : ''}
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            load();
            navigate(`/parking-lot/${id}${search}`);
          }}
        />
      ) : null}
    </div>
  );
}

function Card({ item, index, colourBy, team, search }: { item: ParkingLotItem; index: number; colourBy: ColourBy; team: boolean; search: string }) {
  const tint = colourBy === 'project' ? `tint-${projectTint(item.projectId)}` : `col-${item.column}`;
  const tilt = item.column === 'building' ? '' : ['tilt-a', 'tilt-b', ''][index % 3];
  return (
    <Link to={`/parking-lot/${item.id}${search}`} className={`park-card sticky ${tint} ${tilt}`}>
      <span className={`park-chip ${isNewProjectIdea(item) ? 'new' : `chip-tint-${projectTint(item.projectId)}`}`}>{chipLabel(item)}</span>
      <span className="park-title">{item.title}</span>
      {item.summary ? <span className="park-summary">{item.summary}</span> : null}
      <span className="park-meta">
        {cardMeta(item)}
        {item.commentCount > 0 ? ` · ${item.commentCount} ${item.commentCount === 1 ? 'comment' : 'comments'}` : ''}
        {team && item.audience === 'client' ? ' · shared with the client' : ''}
      </span>
    </Link>
  );
}

/** New idea: type a title and a line, or talk it through with Jabberr instead (it drafts the note at the end). */
function NewIdea({
  team,
  projects,
  defaultProject,
  onClose,
  onCreated,
}: {
  team: boolean;
  projects: Array<{ id: string; name: string; kind: string; role?: string }>;
  defaultProject: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const navigate = useNavigate();
  const { refreshProjects } = useSession();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [projectId, setProjectId] = useState(defaultProject);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  // The team adds to projects they are members of; anyone else to the projects they are a guest in.
  const choices = projects.filter((p) => p.kind === 'team' && (team ? p.role !== 'guest' : true));

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { item } = await api.parkingLot.create({ title: title.trim(), summary: summary.trim(), projectId: projectId || null });
      // A new project idea made an idea project: the session's list (the nav, Layout's current project) learns of it.
      if (item.ideaProject) await refreshProjects().catch(() => undefined);
      onCreated(item.id);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  // Talk first (the default way): Jabberr asks, then writes the note. A new project idea gets its own private idea
  // project; the conversation page shows the "Write the note" bar (?idea=1).
  const talk = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parkingLot.startIdea({ title: title.trim(), summary: summary.trim(), projectId: projectId || null });
      // The conversation opens in the new idea project: Layout must know the project before it gets there.
      if (r.ideaProject) await refreshProjects().catch(() => undefined);
      navigate(`/p/${r.projectId}/c/${r.conversationId}?idea=1`, { state: { firstTurn: r.firstTurn } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="note-layer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form role="dialog" aria-modal="true" aria-label="New idea" className="note-card sticky col-someday new-idea" onSubmit={(e) => void submit(e)}>
        <div className="note-top">
          <h1 className="note-title">New idea</h1>
          <button type="button" className="note-close" aria-label="Close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        <div className="note-edit">
          <p className="note-lead">Tell Jabberr about it. It asks a few questions, then writes the note for you to check.</p>
          <label>
            <span>What is it? (a few words)</span>
            <input autoFocus value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="A short name for the idea" />
          </label>
          <label>
            <span>For</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{team ? 'A new project (its own private idea project)' : 'A new project'}</option>
              {choices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <div className="note-edit-actions">
            <button type="button" className="note-btn primary" disabled={busy} onClick={() => void talk()}>
              Talk it through with Jabberr
            </button>
            {typing ? null : (
              <button type="button" className="note-btn" onClick={() => setTyping(true)}>
                I'd rather type it
              </button>
            )}
          </div>
          {typing ? (
            <>
              <label>
                <span>In one line</span>
                <input value={summary} maxLength={200} onChange={(e) => setSummary(e.target.value)} placeholder="Who it is for and why, in a sentence" />
              </label>
              <div className="note-edit-actions">
                <button type="submit" className="note-btn" disabled={busy || !title.trim()}>
                  Add to parking lot
                </button>
              </div>
            </>
          ) : null}
          <p className="note-hint">{team ? 'The whole team will see the note. A new project idea stays private until you invite people.' : 'You and Tarawa will see it, and the people in the project you pick.'}</p>
        </div>
      </form>
    </div>
  );
}
